/**
 * masterQualificationCheck.js
 *
 * FT-201: Pure function to check Master qualification for a given vessel
 * CERTIFICATES ARE SOURCE OF TRUTH - position is operational label only
 *
 * Checks: (1) Valid CoC for category, (2) Correct medical type, (3) Sea survival
 * All with expiry validation + near-expiry warnings
 *
 * WBC3 References:
 * - Table A5.1: Master qualifications by category
 * - 28.1.3: All CoCs revalidate every 5 years
 * - 28.1.4: Medical fitness (ENG1/ML5)
 * - Note 3: RYA certs require commercial endorsement for workboat use
 *
 * OUT OF SCOPE (per v3 approval):
 * - Collective crew compliance (Mate qualifications for Cat 0-1, Second-in-Command)
 *   remains under existing FT-158 logic in crewCertCheck.js - unchanged
 *
 * FleetSkipper - Built for Mariners.
 */

import { parseCategory } from '../categoryHelpers';
import { calculateExpiryStatus, getDaysUntilExpiry } from './certificateUtils';

// =============================================================================
// CERTIFICATE STRENGTH ORDERING (for preference when multiple certs match)
// =============================================================================

/**
 * Certificate type hierarchy for determining "strongest" qualification
 * Higher number = stronger qualification
 *
 * Per clarification #2: When crew has multiple matching certs, prefer strongest
 * STCW > MCA/Boatmaster > RYA-Commercial > RYA-Leisure (never valid for commercial)
 */
const CERT_STRENGTH = {
  // STCW certificates - highest strength (internationally recognized)
  'stcw master': 100,
  'stcw mate': 90,

  // MCA certificates
  'mca master': 85,
  'boatmaster': 80,
  'boatmasters': 80,

  // RYA with commercial endorsement - mid strength
  'yachtmaster ocean': 75,
  'yachtmaster offshore': 70,
  'yachtmaster coastal': 65,
  'advanced powerboat commercial': 60,
  'powerboat advanced': 60,
  'day skipper': 50,  // Commercial endorsed
  'powerboat level 2 commercial': 45,

  // Competent/Local Authority - variable
  'competent authority': 40,
  'local authority': 35,
};

/**
 * Get strength score for a certificate (higher = stronger)
 */
function getCertStrength(certType) {
  const type = (certType || '').toLowerCase();
  for (const [pattern, strength] of Object.entries(CERT_STRENGTH)) {
    if (type.includes(pattern)) {
      return strength;
    }
  }
  return 0; // Unknown cert type
}

// =============================================================================
// CERTIFICATE LOOKUP TABLES (per WBC3 Table A5.1)
// =============================================================================

/**
 * Per-category CoC lookup (explicit, from WBC3 Table A5.1)
 *
 * VERIFIED against WBC3: Higher category certs ARE valid for lower category vessels
 * (Cat 0 cert holder can work Cat 6 vessel - the table shows checkmarks across)
 *
 * We enumerate certs per category, then inheritance is handled by checking
 * categories 0 through vesselCategory for matches.
 */
const COC_BY_CATEGORY = {
  0: [
    // STCW - highest strength, internationally valid
    'STCW Master (Workboat <500GT)', 'STCW Master <500GT', 'STCW Master Workboat',
    'STCW Master (Code Vessels <200GT unlimited)', 'STCW Master <200GT',
    // RYA Ocean - requires commercial endorsement
    'RYA Yachtmaster Ocean', 'Yachtmaster Ocean CoC', 'Yachtmaster Ocean'
  ],
  1: [
    'STCW Master (Code Vessels <200GT 150nm limit)', 'Master 150nm',
    'RYA Yachtmaster Offshore', 'Yachtmaster Offshore'
  ],
  2: [
    'Boatmaster License', 'Boatmaster Licence', 'Boatmasters', 'Boatmaster Tier 1', 'Boatmaster Tier 2',
    'RYA Yachtmaster Coastal', 'Yachtmaster Coastal'
  ],
  3: [
    'RYA Advanced Powerboat Commercial', 'Powerboat Advanced',
    'Competent Authority Certificate', 'Competent Authority'
  ],
  4: [
    'RYA Day Skipper', 'Day Skipper',
    'RYA Advanced Powerboat Commercial', 'Powerboat Advanced',
    'Competent Authority Certificate', 'Competent Authority'
  ],
  5: [
    'RYA Advanced Powerboat Commercial', 'Powerboat Advanced'
  ],
  6: [
    'RYA Day Skipper', 'Day Skipper',
    'RYA Powerboat Level 2 Commercial', 'RYA Powerboat Level 2', 'Powerboat Level 2',  // FT-201: Added exact MEA production string
    'Local Authority License', 'Local Authority Licence'
  ]
};

// STCW certs - have base cert expiry (5-year revalidation per WBC3 28.1.3)
const STCW_COC_PATTERNS = [
  'stcw master', 'stcw mate', 'boatmaster'
];

// RYA leisure certs - base is perpetual, commercial endorsement expires
// For commercial workboat use, we MUST have valid commercial endorsement (WBC3 Note 3)
const RYA_LEISURE_PATTERNS = [
  'yachtmaster ocean', 'yachtmaster offshore', 'yachtmaster coastal',
  'day skipper', 'powerboat level 2', 'powerboat advanced'
];

// Medical certificates (per WBC3 28.1.4, 28.1.5, MGN 640)
const ENG1_CERTS = ['ENG1', 'ENG1 Medical Certificate', 'ENG1 Medical'];
const ML5_CERTS = ['ML5', 'ML5 Medical Certificate', 'ML5 Medical'];

// Sea survival certificates (per WBC3 28.1.4)
// FT-201: Added 'Personal Survival Techniques (PST)' - exact production string
const SEA_SURVIVAL_CERTS = [
  'STCW Personal Survival Techniques', 'STCW PST', 'PST',
  'Personal Survival Techniques',
  'Personal Survival Techniques (PST)',  // Exact production format
  'RYA Basic Sea Survival', 'Basic Sea Survival',
  'Sea Survival Training', 'Sea Survival'
];

// =============================================================================
// MAIN QUALIFICATION FUNCTION
// =============================================================================

/**
 * Check if a crew member is qualified to serve as Master on a given vessel
 *
 * POSITION IS NOT CHECKED - certificates are the source of truth
 * Any crew member with valid certs can serve as Master regardless of their
 * assigned position. Position is an operational label, not a qualification gate.
 *
 * @param {Object} crewMember - Full crew record with certificates array
 * @param {Object} vessel - Vessel record with category, tonnage, internationalVoyages
 * @returns {{
 *   qualified: boolean,
 *   reasons: string[],           // BLOCKING failures (expired/missing)
 *   warnings: string[],          // Near-expiry alerts (<90 days)
 *   qualifyingCerts: Object[]    // Certs that qualified them (for audit trail)
 * }}
 */
export function isMasterQualified(crewMember, vessel) {
  const reasons = [];
  const warnings = [];
  const qualifyingCerts = [];

  // FAIL CLOSED: If category unknown, use Cat 0 (most restrictive)
  // Per clarification #1: Do NOT default to permissive
  // NOTE: parseCategory() returns 6 for undefined/null, but we want 0 for fail-closed safety
  const vesselCategory = vessel?.category;
  const category = (vesselCategory === null || vesselCategory === undefined || vesselCategory === '')
    ? 0  // MOST RESTRICTIVE - fail closed when category not specified
    : parseCategory(vesselCategory);

  const certs = crewMember?.certificates || [];

  if (certs.length === 0) {
    return {
      qualified: false,
      reasons: ['No certificates on record'],
      warnings: [],
      qualifyingCerts: []
    };
  }

  // =========================================================================
  // 1. Check CoC (with expiry) - ITERATE ALL MATCHES, PREFER STRONGEST
  // =========================================================================

  // Per clarification #2: Don't use .find() - check ALL matching certs
  // and select the strongest valid one

  // Build list of ALL acceptable CoCs for this category (this + all lower categories)
  // Per WBC3 Table A5.1: Cat 0 cert can work Cat 6 vessel
  const acceptableCoCs = [];
  for (let cat = 0; cat <= category; cat++) {
    acceptableCoCs.push(...(COC_BY_CATEGORY[cat] || []));
  }

  // Find ALL matching CoC certs
  const matchingCoCCerts = certs.filter(c => {
    const type = (c.type || c.title || '').toLowerCase();
    return acceptableCoCs.some(valid => type.includes(valid.toLowerCase()));
  });

  // Sort by strength (highest first)
  matchingCoCCerts.sort((a, b) =>
    getCertStrength(b.type || b.title) - getCertStrength(a.type || a.title)
  );

  let validCoCFound = false;
  let cocProcessed = false;

  // Process certs in strength order, take first valid one
  for (const cocCert of matchingCoCCerts) {
    const certType = (cocCert.type || cocCert.title || '').toLowerCase();
    const isSTCW = STCW_COC_PATTERNS.some(p => certType.includes(p));
    const isRYALeisure = RYA_LEISURE_PATTERNS.some(p => certType.includes(p));
    const expiryDate = cocCert.expiry_date || cocCert.expiryDate;

    if (isSTCW) {
      // STCW certs have 5-year base cert expiry (WBC3 28.1.3)
      if (!expiryDate) {
        warnings.push(`CoC ${cocCert.type || cocCert.title} has no expiry date recorded - verify 5-year revalidation`);
        // Still consider valid but flagged
        validCoCFound = true;
        qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
        cocProcessed = true;
        break;
      } else {
        const status = calculateExpiryStatus(expiryDate);
        if (status === 'expired') {
          // Don't break - try next cert in strength order
          continue;
        } else if (status === 'warning' || status === 'critical') {
          const days = getDaysUntilExpiry(expiryDate);
          warnings.push(`CoC revalidation due in ${days} days (${cocCert.type || cocCert.title})`);
          validCoCFound = true;
          qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
          cocProcessed = true;
          break;
        } else {
          validCoCFound = true;
          qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
          cocProcessed = true;
          break;
        }
      }
    } else if (isRYALeisure) {
      // RYA leisure certs: base is perpetual, but commercial endorsement expires
      // For workboat commercial use, we require the endorsement (WBC3 Note 3)

      // FT-201 MEA Fix: Commercial endorsement can be:
      // 1. In the cert name itself (e.g., "RYA Powerboat Level 2 Commercial")
      // 2. OR a SEPARATE cert called "RYA Commercial Endorsement"
      const hasCommercialInName = certType.includes('commercial');

      // Check for separate commercial endorsement cert
      const separateEndorsement = certs.find(c => {
        const t = (c.type || c.title || '').toLowerCase();
        return t.includes('commercial endorsement') || t.includes('rya commercial');
      });
      const hasValidSeparateEndorsement = separateEndorsement && (() => {
        const sepExpiry = separateEndorsement.expiry_date || separateEndorsement.expiryDate;
        if (!sepExpiry) return true; // No expiry recorded = assume valid but will warn
        return calculateExpiryStatus(sepExpiry) !== 'expired';
      })();

      if (!expiryDate && !hasCommercialInName && !hasValidSeparateEndorsement) {
        // Likely a leisure cert without commercial endorsement - NOT valid
        // Per clarification #3: This is different from expired endorsement
        // Don't break - try next cert
        continue;
      } else if (hasValidSeparateEndorsement && !hasCommercialInName) {
        // FT-201: RYA leisure cert with SEPARATE valid commercial endorsement
        const sepExpiry = separateEndorsement.expiry_date || separateEndorsement.expiryDate;
        if (sepExpiry) {
          const status = calculateExpiryStatus(sepExpiry);
          if (status === 'warning' || status === 'critical') {
            const days = getDaysUntilExpiry(sepExpiry);
            warnings.push(`Commercial endorsement expires in ${days} days`);
          }
        } else {
          warnings.push(`Commercial endorsement has no expiry recorded - verify validity`);
        }
        validCoCFound = true;
        qualifyingCerts.push({
          type: 'coc',
          cert: { ...cocCert },
          strength: getCertStrength(certType),
          endorsement: { ...separateEndorsement }
        });
        cocProcessed = true;
        break;
      } else if (expiryDate) {
        // Has expiry - treat as commercial endorsement expiry
        const status = calculateExpiryStatus(expiryDate);
        if (status === 'expired') {
          // Per clarification #3: Expired endorsement - different from no endorsement
          // Don't break - try next cert
          continue;
        } else if (status === 'warning' || status === 'critical') {
          const days = getDaysUntilExpiry(expiryDate);
          warnings.push(`Commercial endorsement expires in ${days} days (${cocCert.type || cocCert.title})`);
          validCoCFound = true;
          qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
          cocProcessed = true;
          break;
        } else {
          validCoCFound = true;
          qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
          cocProcessed = true;
          break;
        }
      } else if (hasCommercialInName) {
        // "Commercial" in name but no expiry - warn but allow
        warnings.push(`${cocCert.type || cocCert.title} has no endorsement expiry recorded - verify validity`);
        validCoCFound = true;
        qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
        cocProcessed = true;
        break;
      }
    } else {
      // Other cert types (Competent Authority, Local Authority) - check expiry if present
      if (expiryDate) {
        const status = calculateExpiryStatus(expiryDate);
        if (status === 'expired') {
          continue;
        } else if (status === 'warning' || status === 'critical') {
          const days = getDaysUntilExpiry(expiryDate);
          warnings.push(`Certificate expires in ${days} days (${cocCert.type || cocCert.title})`);
          validCoCFound = true;
          qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
          cocProcessed = true;
          break;
        } else {
          validCoCFound = true;
          qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
          cocProcessed = true;
          break;
        }
      } else {
        // No expiry - allow with warning
        warnings.push(`${cocCert.type || cocCert.title} has no expiry recorded`);
        validCoCFound = true;
        qualifyingCerts.push({ type: 'coc', cert: { ...cocCert }, strength: getCertStrength(certType) });
        cocProcessed = true;
        break;
      }
    }
  }

  // If no valid CoC found after checking all matching certs
  if (!validCoCFound) {
    if (matchingCoCCerts.length > 0) {
      // Had matching certs but all were expired/invalid
      const failedCerts = matchingCoCCerts.map(c => {
        const type = c.type || c.title || 'Unknown';
        const expiryDate = c.expiry_date || c.expiryDate;
        const certType = type.toLowerCase();
        const isRYALeisure = RYA_LEISURE_PATTERNS.some(p => certType.includes(p));
        const hasCommercialInName = certType.includes('commercial');

        if (isRYALeisure && !expiryDate && !hasCommercialInName) {
          return `${type} (leisure-only, no commercial endorsement)`;
        } else if (expiryDate) {
          const status = calculateExpiryStatus(expiryDate);
          if (status === 'expired') {
            return `${type} (expired)`;
          }
        }
        return type;
      });
      reasons.push(`CoC(s) found but not valid: ${failedCerts.join(', ')}`);
    } else {
      reasons.push(`Missing Certificate of Competency valid for Category ${category} operations`);
    }
  }

  // =========================================================================
  // 2. Check Medical (ENG1 for Cat 0-1/intl, ML5 for Cat 2-6 domestic)
  // =========================================================================

  const needsENG1 = category <= 1 || vessel?.internationalVoyages === true;
  const medicalTypes = needsENG1 ? ENG1_CERTS : ML5_CERTS;
  const medicalCert = certs.find(c => {
    const type = (c.type || c.title || '').toLowerCase();
    return medicalTypes.some(m => type.includes(m.toLowerCase()));
  });

  if (!medicalCert) {
    const required = needsENG1 ? 'ENG1' : 'ML5';
    const reason = needsENG1
      ? `Missing ENG1 Medical Certificate (required for Cat 0-1 or international voyages)`
      : `Missing ML5 Medical Certificate (required for Cat 2-6 domestic operations)`;
    reasons.push(reason);
  } else {
    const expiryDate = medicalCert.expiry_date || medicalCert.expiryDate;
    if (!expiryDate) {
      warnings.push(`Medical certificate has no expiry date recorded`);
      qualifyingCerts.push({ type: 'medical', cert: { ...medicalCert } });
    } else {
      const status = calculateExpiryStatus(expiryDate);
      if (status === 'expired') {
        reasons.push(`Medical expired: ${medicalCert.type || medicalCert.title}`);
      } else if (status === 'warning' || status === 'critical') {
        const days = getDaysUntilExpiry(expiryDate);
        warnings.push(`Medical expires in ${days} days`);
        qualifyingCerts.push({ type: 'medical', cert: { ...medicalCert } });
      } else {
        qualifyingCerts.push({ type: 'medical', cert: { ...medicalCert } });
      }
    }
  }

  // =========================================================================
  // 3. Check Sea Survival (WBC3 28.1.4)
  // =========================================================================

  const survivalCert = certs.find(c => {
    const type = (c.type || c.title || '').toLowerCase();
    return SEA_SURVIVAL_CERTS.some(s => type.includes(s.toLowerCase()));
  });

  if (!survivalCert) {
    reasons.push('Missing Sea Survival certificate (PST required per WBC3 28.1.4)');
  } else {
    const expiryDate = survivalCert.expiry_date || survivalCert.expiryDate;
    if (expiryDate) {
      const status = calculateExpiryStatus(expiryDate);
      if (status === 'expired') {
        reasons.push(`Sea Survival expired: ${survivalCert.type || survivalCert.title}`);
      } else if (status === 'warning' || status === 'critical') {
        const days = getDaysUntilExpiry(expiryDate);
        warnings.push(`Sea Survival expires in ${days} days`);
        qualifyingCerts.push({ type: 'survival', cert: { ...survivalCert } });
      } else {
        qualifyingCerts.push({ type: 'survival', cert: { ...survivalCert } });
      }
    } else {
      // No expiry - PST is typically 5-year, warn if missing
      warnings.push(`Sea Survival has no expiry date recorded - verify validity`);
      qualifyingCerts.push({ type: 'survival', cert: { ...survivalCert } });
    }
  }

  return {
    qualified: reasons.length === 0,
    reasons,
    warnings,
    qualifyingCerts  // For audit trail - store which certs qualified at designation time
  };
}

/**
 * Get all crew members who are qualified to serve as Master
 *
 * Convenience wrapper that checks all crew and returns qualified ones.
 * Used by pre-departure dropdown to populate Master selection list.
 *
 * @param {Array} crewMembers - Array of crew member objects
 * @param {Object} vessel - Vessel settings with category
 * @returns {Array} Array of { crew, qualificationResult } for qualified crew only
 */
export function getQualifiedMasters(crewMembers, vessel) {
  if (!Array.isArray(crewMembers) || crewMembers.length === 0) {
    return [];
  }

  const results = crewMembers.map(crew => ({
    crew,
    qualificationResult: isMasterQualified(crew, vessel)
  }));

  return results.filter(r => r.qualificationResult.qualified);
}

/**
 * Build audit trail snapshot for Master designation
 *
 * Per clarification #4: This must be called at pre-departure form submission
 * (when masterId is set), NOT at LET GO time.
 *
 * @param {Object} crewMember - The designated Master
 * @param {Object} vessel - Vessel settings
 * @param {string} designatedById - User ID who made the designation
 * @returns {Object} Immutable snapshot for voyage.masterDesignation
 */
export function createMasterDesignationSnapshot(crewMember, vessel, designatedById) {
  const qualificationResult = isMasterQualified(crewMember, vessel);

  return {
    masterName: crewMember?.name || crewMember?.full_name || null,
    designatedAt: new Date().toISOString(),
    designatedBy: designatedById,
    qualifyingCerts: qualificationResult.qualifyingCerts.map(qc => ({
      type: qc.type,
      certId: qc.cert?.id || null,
      certType: qc.cert?.type || qc.cert?.title || null,
      expiryDate: qc.cert?.expiry_date || qc.cert?.expiryDate || null,
      strength: qc.strength || null
    })),
    vesselCategory: parseCategory(vessel?.category) || 0,
    warningsAtDesignation: qualificationResult.warnings
  };
}

export default isMasterQualified;
